// Cache Design System — supports light + dark themes
// Direction: warm / editorial (NYT Cooking, Linear) — NOT financial-terminal
// Fonts: DM Sans (body + headings) + IBM Plex Mono (numerics) via expo-google-fonts
//
// `createCommonStyles(theme)` is the factory; consumers call
// `useCommonStyles()` (in src/styles/useCommonStyles.ts) which memoizes
// per active theme.

import { StyleSheet } from 'react-native';
import { type Theme } from './theme';

export function createCommonStyles(theme: Theme) {
  return StyleSheet.create({
  // ----- Layout -----
  safeArea: {
    flex: 1,
    backgroundColor: theme.colors.backgroundPrimary,
  },

  screenContainer: {
    flex: 1,
    backgroundColor: theme.colors.backgroundPrimary,
    paddingHorizontal: theme.spacing.lg,
  },

  card: {
    backgroundColor: theme.colors.backgroundSurface,
    borderRadius: theme.radius.radiusMd,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.borders.borderColor,
    padding: theme.spacing.lg,
    ...theme.shadows.cardShadow,
  },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  rowSpaceBetween: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  divider: {
    height: 1,
    backgroundColor: theme.borders.borderColor,
    marginVertical: theme.spacing.md,
  },

  // ----- Typography -----
  sectionLabel: {
    ...theme.typography.xs,
    color: theme.colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },

  screenTitle: {
    ...theme.typography.h2,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  screenSubtitle: {
    ...theme.typography.lg,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xl,
    textAlign: 'center',
  },

  // ----- Read-only field (display non-editable values) -----
  // Default uses backgroundSurface. When nested inside a surface-bg card,
  // override with backgroundColor: theme.colors.backgroundElevated.
  readOnlyField: {
    backgroundColor: theme.colors.backgroundSurface,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.borders.borderColor,
    borderRadius: theme.radius.radiusMd,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    marginTop: theme.spacing.sm,
  },
  readOnlyText: {
    ...theme.typography.md,
    color: theme.colors.textPrimary,
  },

  // ----- Value text (numbers / currency) -----
  valueDisplay: {
    ...theme.typography.valueLg,
    color: theme.colors.accent,
  },
  valuePrimary: {
    ...theme.typography.valueLg,
    color: theme.colors.textPrimary,
  },
  valueDisplayMd: {
    ...theme.typography.valueMd,
    color: theme.colors.accent,
  },
  valuePrimaryMd: {
    ...theme.typography.valueMd,
    color: theme.colors.textPrimary,
  },
  valueDisplaySm: {
    ...theme.typography.valueSm,
    color: theme.colors.accent,
  },
  valuePrimarySm: {
    ...theme.typography.valueSm,
    color: theme.colors.textPrimary,
  },

  // ----- Buttons -----
  primaryButton: {
    backgroundColor: theme.colors.accent,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.radius.radiusMd,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    ...theme.typography.md,
    fontFamily: theme.fontFamily.bodyMedium,
    color: theme.colors.backgroundPrimary,
  },

  secondaryButton: {
    backgroundColor: 'transparent',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.radius.radiusMd,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.borders.borderColor,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    ...theme.typography.md,
    fontFamily: theme.fontFamily.bodyMedium,
    color: theme.colors.textPrimary,
  },

  destructiveButton: {
    backgroundColor: theme.colors.destructive,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.radius.radiusMd,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
  },
  destructiveButtonText: {
    ...theme.typography.md,
    fontFamily: theme.fontFamily.bodyMedium,
    color: theme.colors.textPrimary,
  },

  disabledButton: {
    opacity: 0.5,
  },

  socialButton: {
    backgroundColor: theme.colors.backgroundSurface,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.radius.radiusMd,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.borders.borderColor,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  socialButtonText: {
    ...theme.typography.md,
    fontFamily: theme.fontFamily.bodyMedium,
    color: theme.colors.textPrimary,
  },

  link: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    alignItems: 'center',
  },
  linkText: {
    ...theme.typography.sm,
    color: theme.colors.accent,
    textDecorationLine: 'underline',
  },

  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  },
  backButtonText: {
    ...theme.typography.md,
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.sm,
  },

  // ----- Form -----
  form: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  formField: {
    marginBottom: theme.spacing.lg,
  },
  input: {
    backgroundColor: theme.colors.backgroundSurface,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.borders.borderColor,
    borderRadius: theme.radius.radiusMd,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    ...theme.typography.md,
    color: theme.colors.textPrimary,
  },
  inputLabel: {
    ...theme.typography.sm,
    fontFamily: theme.fontFamily.bodyMedium,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  inputError: {
    borderColor: theme.colors.destructive,
  },
  formErrorText: {
    ...theme.typography.sm,
    color: theme.colors.destructive,
    textAlign: 'center',
    marginVertical: theme.spacing.sm,
  },

  // ----- Banners -----
  errorContainer: {
    backgroundColor: theme.colors.backgroundSurface,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.colors.destructive,
    borderRadius: theme.radius.radiusMd,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
  },
  errorText: {
    ...theme.typography.md,
    color: theme.colors.destructive,
  },
  notificationContainer: {
    backgroundColor: theme.colors.backgroundSurface,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.colors.accent,
    borderRadius: theme.radius.radiusMd,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
  },
  notificationText: {
    ...theme.typography.md,
    color: theme.colors.textPrimary,
  },

  // ----- Modal / dialog -----
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  modalContent: {
    backgroundColor: theme.colors.backgroundElevated,
    borderRadius: theme.radius.radiusLg,
    borderWidth: theme.borders.borderWidth,
    borderColor: theme.borders.borderColor,
    padding: theme.spacing.xxl,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    ...theme.typography.h4,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  modalText: {
    ...theme.typography.lg,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xl,
  },

  // ----- Misc -----
  separator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: theme.spacing.lg,
  },
  separatorLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.borders.borderColor,
  },
  separatorText: {
    ...theme.typography.sm,
    color: theme.colors.textSecondary,
    marginHorizontal: theme.spacing.md,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13, 13, 13, 0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
  },
  });
}
